<script setup lang="ts">
import { ref, provide, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useMatterStore } from '../../stores/matterStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useChatStore } from '../../stores/chatStore';
import { useDataViewStore } from '../../stores/dataViewStore';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import { useDataLoadStore } from '../../stores/dataLoadStore';
import { useDataInventoryStore } from '../../stores/dataInventoryStore';
import { useDialoguePreferencesStore } from '../../stores/dialoguePreferencesStore';
import DialogSessionList from './DialogSessionList.vue';
import DataInventoryPanel from './DataInventoryPanel.vue';
import DialoguePreferencesDialog from './DialoguePreferencesDialog.vue';
import MatterNavRail from './MatterNavRail.vue';
import ChatWindow from '../chat/ChatWindow.vue';
import ChatInput from '../chat/ChatInput.vue';
import ReferencesPanel from '../references/ReferencesPanel.vue';
import RawViewContainer from '../references/RawViewContainer.vue';
import DETDebugView from '../debug/DETDebugView.vue';
import { useReferencesViewUrlSync } from '../../composables/useReferencesViewUrlSync';
import { useResizableSidebar } from '../../composables/useResizableSidebar';

useReferencesViewUrlSync();

const { t } = useI18n();
const matterStore = useMatterStore();
const sessionStore = useSessionStore();
const chatStore = useChatStore();
const dataViewStore = useDataViewStore();
const matterContextModalStore = useMatterContextModalStore();
const router = useRouter();
const route = useRoute();
const dataLoadStore = useDataLoadStore();
const dataInventoryStore = useDataInventoryStore();
const dialoguePreferencesStore = useDialoguePreferencesStore();

// "Dialogue preferences" dialog, opened from the gear in the chat header.
const showDialoguePreferences = ref(false);

// On opening a matter we land on the new-chat route (no :sessionId) so the
// page shows immediately without waiting on the session list. Once sessions
// load, redirect to the most recent dialogue if one exists — but only while
// the user is still sitting on that same new-chat route. If they've already
// picked a session, started a new chat elsewhere, or left the matter, leave
// them be. A no-session matter stays on new-chat. Failures are swallowed:
// the new-chat landing is a fine fallback. Skipped entirely when the user's
// dialogue preference is to always start on a new chat.
onMounted(async () => {
    if (route.name !== 'matter') return;
    if (dialoguePreferencesStore.matterSelectionBehavior !== 'most-recent') return;
    const matterId = matterStore.currentMatter?.RecordId;
    if (!matterId) return;

    try {
        await sessionStore.loadSessions();
    } catch {
        return;
    }

    // Bail if anything changed while the list was loading.
    if (
        router.currentRoute.value.name !== 'matter' ||
        router.currentRoute.value.params.matterId !== matterId ||
        sessionStore.isSessionSelected
    ) {
        return;
    }

    const recent = sessionStore.mostRecentSession;
    if (!recent) return;

    sessionStore.selectSession(recent);
    router.replace({
        name: 'session',
        params: { matterId, sessionId: recent.RecordId },
    });
});

// Both flags are store-backed so the URL sync composable can drive them
// on cold deep links without going through provide/inject.
const { isDetailsViewerVisible: showDetails, showDetailView } = storeToRefs(dataViewStore);
const referencesCollapsed = ref(false);

// References "full width" mode: the references panel fills the whole content
// area and the chat collapses to a vertical "Chat" tab on the left edge that
// restores the split when clicked. Toggled from the panel header's split icon.
// Only meaningful while the references panel is open and the raw detail drawer
// isn't (the drawer has its own layout); reset whenever either closes.
const referencesFullWidth = ref(false);

// How many right-hand data panels are currently taking horizontal space.
// Drives the nav sidebar's automatic collapse/expand below.
const openSidePanelCount = computed(
    () => (showDetails.value ? 1 : 0) + (showDetailView.value ? 1 : 0),
);

// Drag-resizable "Recents" sidebar. Width persists; its collapsed state is
// derived from how many side panels are open (with a resettable manual
// override). When collapsed the icon rail stays and exposes an expand button.
const {
    width: sidebarWidth,
    collapsed: navCollapsed,
    startResize: startSidebarResize,
    toggleCollapsed: toggleNav,
} = useResizableSidebar(() => openSidePanelCount.value);

// Until the matter has published context, the whole page is locked behind
// an overlay (see chatStore.matterContextRequired). This button reopens
// the onboarding modal if the user dismissed it.
const openMatterContext = () => {
    const matter = matterStore.currentMatter;
    if (matter) matterContextModalStore.open(matter.RecordId);
};

const openDataLoad = () => {
    const matter = matterStore.currentMatter;
    if (matter) dataLoadStore.open(matter.RecordId);
};

// Escape hatch from the locked matter back to the matters list.
const goToMatters = () => {
    router.push({ name: 'matter-selection' });
};

// "+" on the collapsed Dialogues rail — start a fresh dialogue without first
// expanding the sidebar. Mirrors DialogSessionList's "New Dialogue" button:
// the matter route (no sessionId) is the new-chat state.
const startNewDialogue = () => {
    const matterId = matterStore.currentMatter?.RecordId;
    if (matterId) router.push({ name: 'matter', params: { matterId } });
};

// Bumped to ask DialogSessionList to open and focus its search box. Used by
// the collapsed-rail search button, which also expands the sidebar first.
const searchTrigger = ref(0);

const requestSearch = () => {
    if (navCollapsed.value) toggleNav();
    searchTrigger.value += 1;
};

const closeDetails = () => {
    referencesCollapsed.value = false;
    referencesFullWidth.value = false;
    dataViewStore.closeDetailsViewer();
};

const toggleReferencesCollapsed = () => {
    referencesCollapsed.value = !referencesCollapsed.value;
};

// Split icon in the references header. Off returns to the 50/50 split; on
// hands the whole content area to references and collapses the chat to a tab.
const toggleReferencesFullWidth = () => {
    referencesFullWidth.value = !referencesFullWidth.value;
};

// Click target for the shared collapsed chat rail. It appears in two modes;
// restore the chat the way each one collapsed it: exit full-width references,
// or (detail view open) fold the references panel to its strip so the chat
// widens back.
const expandCollapsedChat = () => {
    if (referencesFullWidth.value) {
        referencesFullWidth.value = false;
    } else {
        referencesCollapsed.value = true;
    }
};

const openDetailView = () => {
    // Keep full-width sticky across selecting a reference: the layout drops out
    // of full-width while the raw panel is open (the references-full-width class
    // is gated on !showDetailView), then returns to it once the reference is
    // closed. Full-width is only reset by picking a different response or
    // leaving the dialogue (see the watchers below).
    dataViewStore.showDetailView = true;
};

const closeDetailView = () => {
    dataViewStore.showDetailView = false;
    referencesCollapsed.value = false;
    dataViewStore.communicationGroups = [];
    dataViewStore.currentGroupIndex = 0;
};

watch(
    () => sessionStore.currentSessionId,
    () => {
        closeDetails();
    },
);

watch(
    () => dataViewStore.selectedChatForDetails,
    () => {
        // A different response's references were opened — full-width doesn't
        // carry over to a new response, so reset it (and close any open raw
        // view from the previous response).
        referencesFullWidth.value = false;
        if (showDetailView.value) {
            closeDetailView();
        }
    },
);

provide('openDetailView', openDetailView);

const detailViewTitle = computed(() => {
    if (dataViewStore.communicationGroups.length === 0) return t('matters.details');

    const currentRef = dataViewStore.communicationGroups[dataViewStore.currentGroupIndex];
    if (!currentRef) return t('matters.details');

    const typeName = currentRef.type || t('matters.detailItemFallback');
    const formattedType = typeName.replace(/([a-z])([A-Z])/g, '$1 $2');

    return `${formattedType}: ${currentRef.display_identifier || ''}`;
});

const hasNavigation = computed(() => dataViewStore.communicationGroups.length > 1);
const hasPrev = computed(() => dataViewStore.currentGroupIndex > 0);
const hasNext = computed(
    () => dataViewStore.currentGroupIndex < dataViewStore.communicationGroups.length - 1,
);
const currentPosition = computed(() => {
    if (!hasNavigation.value) return '';
    return t('matters.positionOf', {
        current: dataViewStore.currentGroupIndex + 1,
        total: dataViewStore.communicationGroups.length,
    });
});

function navigatePrev() {
    if (hasPrev.value) {
        dataViewStore.currentGroupIndex--;
    }
}

function navigateNext() {
    if (hasNext.value) {
        dataViewStore.currentGroupIndex++;
    }
}

// referenced via template only
void matterStore;
</script>

<template>
    <div class="matter-page">
        <div
            v-if="chatStore.matterContextRequired && !matterContextModalStore.visible"
            class="matter-locked-overlay"
            role="dialog"
            aria-modal="true"
        >
            <div class="matter-locked-card">
                <span class="material-icons-outlined matter-locked-icon">lock</span>
                <h2 class="matter-locked-title">{{ t('matters.locked.contextTitle') }}</h2>
                <p class="matter-locked-message">
                    {{ t('matters.locked.contextMessage') }}
                </p>
                <div class="matter-locked-actions">
                    <button class="matter-locked-button" type="button" @click="openMatterContext">
                        <span class="material-icons">edit_note</span>
                        <span>{{ t('matters.locked.setUpContext') }}</span>
                    </button>
                    <button
                        class="matter-locked-button matter-locked-button-secondary"
                        type="button"
                        @click="goToMatters"
                    >
                        <span class="material-icons">arrow_back</span>
                        <span>{{ t('matters.locked.returnToMatters') }}</span>
                    </button>
                </div>
            </div>
        </div>

        <div
            v-else-if="dataLoadStore.scanRequired && !dataLoadStore.isOpen"
            class="matter-locked-overlay"
            role="dialog"
            aria-modal="true"
        >
            <div class="matter-locked-card">
                <span class="material-icons-outlined matter-locked-icon">storage</span>
                <h2 class="matter-locked-title">{{ t('matters.locked.scanTitle') }}</h2>
                <p class="matter-locked-message">
                    {{ t('matters.locked.scanMessage') }}
                </p>
                <div class="matter-locked-actions">
                    <button class="matter-locked-button" type="button" @click="openDataLoad">
                        <span class="material-icons">manage_search</span>
                        <span>{{ t('matters.locked.runScan') }}</span>
                    </button>
                    <button
                        class="matter-locked-button matter-locked-button-secondary"
                        type="button"
                        @click="goToMatters"
                    >
                        <span class="material-icons">arrow_back</span>
                        <span>{{ t('matters.locked.returnToMatters') }}</span>
                    </button>
                </div>
            </div>
        </div>

        <MatterNavRail />

        <transition name="mc-strip">
            <nav
                v-if="navCollapsed"
                class="dialogues-nav-collapsed"
                :aria-label="t('matters.dialoguesNav.collapsedAria')"
            >
                <button
                    type="button"
                    class="dialogues-collapsed-item"
                    :title="t('matters.dialoguesNav.expand')"
                    :aria-label="t('matters.dialoguesNav.expand')"
                    @click="toggleNav"
                >
                    <span class="material-icons">chevron_right</span>
                </button>

                <button
                    type="button"
                    class="dialogues-collapsed-item dialogues-collapsed-item-disabled"
                    :title="t('matters.dialoguesNav.foldersTitle')"
                    :aria-label="t('matters.dialoguesNav.foldersAria')"
                    disabled
                >
                    <span class="material-icons-outlined">folder</span>
                </button>

                <button
                    type="button"
                    class="dialogues-collapsed-item"
                    :title="t('matters.dialoguesNav.newDialogue')"
                    :aria-label="t('matters.dialoguesNav.newDialogue')"
                    @click="startNewDialogue"
                >
                    <span class="material-icons">add</span>
                </button>

                <button
                    type="button"
                    class="dialogues-collapsed-item"
                    :title="t('matters.dialoguesNav.search')"
                    :aria-label="t('matters.dialoguesNav.search')"
                    @click="requestSearch"
                >
                    <span class="material-icons">search</span>
                </button>
            </nav>
        </transition>

        <transition name="mc-sidebar">
            <div v-if="!navCollapsed" class="sidebar" :style="{ width: sidebarWidth + 'px' }">
                <div class="matter-sidebar-header">
                    <button
                        type="button"
                        class="sidebar-collapse-btn"
                        :title="t('matters.sidebar.collapse')"
                        :aria-label="t('matters.sidebar.collapse')"
                        @click="toggleNav"
                    >
                        <!-- Arrow into the right-edge bar — "collapse this panel
                             to the left" (matches the design glyph). -->
                        <svg class="nav-toggle-icon" viewBox="0 0 24 24" aria-hidden="true">
                            <line x1="19" y1="5" x2="19" y2="19" />
                            <line x1="13" y1="12" x2="4" y2="12" />
                            <polyline points="8 8 4 12 8 16" />
                        </svg>
                    </button>
                </div>
                <DialogSessionList :search-trigger="searchTrigger" />
                <div
                    class="sidebar-resize-handle"
                    :title="t('matters.sidebar.resize')"
                    @mousedown="startSidebarResize"
                ></div>
            </div>
        </transition>

        <div
            class="main-content"
            :class="{
                'details-open': showDetails,
                'detail-view-open': showDetailView,
                'references-collapsed': referencesCollapsed,
                'references-full-width': referencesFullWidth && showDetails && !showDetailView,
            }"
        >
            <!-- Full-width session header: spans the whole content area above
                 every panel (chat / references / raw), with the dialogue title
                 on the left and the dialogue-preferences gear pinned far right.
                 Lives here rather than inside chat-section so it no longer
                 narrows to the chat column. -->
            <div class="session-header">
                <h2 class="session-header-title">
                    {{ sessionStore.currentSession?.Name || t('matters.newChat') }}
                </h2>
                <span
                    class="material-icons details-close-icon session-header-gear"
                    :title="t('dialoguePreferences.title')"
                    role="button"
                    tabindex="0"
                    @click="showDialoguePreferences = true"
                    @keydown.enter="showDialoguePreferences = true"
                    @keydown.space.prevent="showDialoguePreferences = true"
                    >settings</span
                >
            </div>

            <div class="main-content-panels">
            <!-- Single collapsed chat rail shared by both scenarios: a reference
                 item is open (detail view) or references is full-width. Keeping
                 it one element means switching between those two modes doesn't
                 animate an out+in swap of two visually identical rails — the
                 node persists and stays put. The click target restores the chat
                 the way that mode collapsed it. -->
            <transition name="mc-strip">
                <div
                    v-if="
                        (showDetails && showDetailView && !referencesCollapsed) ||
                        (referencesFullWidth && showDetails && !showDetailView)
                    "
                    class="chat-section-collapsed"
                    role="button"
                    tabindex="0"
                    :title="t('matters.expandChat')"
                    @click="expandCollapsedChat"
                    @keydown.enter="expandCollapsedChat"
                    @keydown.space.prevent="expandCollapsedChat"
                >
                    <!-- Lucide unfold-horizontal: the collapsed rail unfolds the
                         chat back out when clicked. -->
                    <svg class="chat-collapsed-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M16 12h6" />
                        <path d="M8 12H2" />
                        <path d="M12 2v2" />
                        <path d="M12 8v2" />
                        <path d="M12 14v2" />
                        <path d="M12 20v2" />
                        <path d="m19 15 3-3-3-3" />
                        <path d="m5 9-3 3 3 3" />
                    </svg>
                    <span class="chat-collapsed-title">{{ t('matters.chat') }}</span>
                </div>
            </transition>

            <div
                class="chat-section"
                :class="{
                    'chat-section-hidden': showDetails && showDetailView && !referencesCollapsed,
                }"
            >
                <ChatWindow />

                <ChatInput v-if="sessionStore.isSessionSelected" />
            </div>

            <transition name="mc-strip">
                <div
                    v-if="showDetails && showDetailView && referencesCollapsed"
                    class="references-section-collapsed"
                    role="button"
                    tabindex="0"
                    :title="t('matters.expandReferences')"
                    @click="toggleReferencesCollapsed"
                    @keydown.enter="toggleReferencesCollapsed"
                    @keydown.space.prevent="toggleReferencesCollapsed"
                >
                    <span class="material-icons-outlined references-collapsed-icon">menu_book</span>
                    <span class="references-collapsed-title">{{ t('matters.references') }}</span>
                </div>
            </transition>

            <transition name="mc-panel">
                <div v-if="showDetails && !referencesCollapsed" class="details-section">
                    <ReferencesPanel
                        :is-full-width="referencesFullWidth && !showDetailView"
                        :can-toggle-full-width="!showDetailView"
                        :can-collapse="showDetailView"
                        @close="closeDetails"
                        @collapse="toggleReferencesCollapsed"
                        @toggle-full-width="toggleReferencesFullWidth"
                    />
                </div>
            </transition>

            <transition name="mc-drawer">
                <div v-if="showDetailView" class="detail-view-section">
                    <div class="detail-view-section-header">
                        <h2>{{ detailViewTitle }}</h2>
                        <div class="header-right">
                            <div v-if="hasNavigation" class="header-navigation">
                                <button
                                    :disabled="!hasPrev"
                                    class="nav-button nav-button-icon"
                                    @click="navigatePrev"
                                >
                                    <span class="material-icons nav-icon">arrow_back</span>
                                </button>
                                <span class="nav-position-text">{{ currentPosition }}</span>
                                <button
                                    :disabled="!hasNext"
                                    class="nav-button nav-button-icon"
                                    @click="navigateNext"
                                >
                                    <span class="material-icons nav-icon">arrow_forward</span>
                                </button>
                            </div>
                            <span
                                class="material-icons details-close-icon"
                                :title="t('common.close')"
                                @click="closeDetailView"
                                >close</span
                            >
                        </div>
                    </div>
                    <div class="detail-view-section-content">
                        <RawViewContainer
                            v-if="dataViewStore.communicationGroups.length > 0"
                            :references="dataViewStore.communicationGroups"
                            :initial-index="dataViewStore.currentGroupIndex"
                            :visible="true"
                            :as-panel="true"
                            :hide-header="true"
                            @close="closeDetailView"
                        />
                    </div>
                </div>
            </transition>
            </div>
        </div>

        <!-- Matter Data Inventory: far-right flex sibling. As a flex child of
             .matter-page it shrinks .main-content rather than overlaying it,
             mirroring the left Recents sidebar. Toggled from the app header. -->
        <transition name="mc-panel">
            <DataInventoryPanel v-if="dataInventoryStore.isOpen" />
        </transition>

        <div
            v-if="chatStore.showDETDebugPanel"
            class="det-debug-overlay"
            @click.self="chatStore.closeDETDebugPanel()"
        >
            <div class="det-debug-overlay-panel">
                <div class="det-debug-overlay-header">
                    <h2>{{ t('matters.workflowTrace') }}</h2>
                    <span
                        class="material-icons det-debug-overlay-close"
                        @click="chatStore.closeDETDebugPanel()"
                        >close</span
                    >
                </div>
                <div class="det-debug-overlay-body">
                    <div
                        v-if="chatStore.detDebugLoading && !chatStore.currentDETDebugTrace"
                        class="det-debug-loading"
                    >
                        <div class="loading-dots"><span></span><span></span><span></span></div>
                        <span>{{ t('matters.loadingWorkflowTrace') }}</span>
                    </div>
                    <DETDebugView v-else />
                </div>
            </div>
        </div>

        <DialoguePreferencesDialog
            v-if="showDialoguePreferences"
            @close="showDialoguePreferences = false"
        />
    </div>
</template>

<style scoped>
/* Smaller than the shared 24px header icons, matching the compact gear in the
   design. */
.session-header-gear {
    font-size: 20px;
}
</style>
